<template>
	<ClientOnly>
		<div
			v-if="ad"
			ref="adRoot"
			class="stock-alert-card"
		>
			<img
				v-if="imageUrl"
				:src="imageUrl"
				:alt="title || 'Stock Alert Card'"
				class="w-full h-full object-cover rounded-lg"
				loading="lazy"
			/>

			<h3 class="text-2xl text-white leading-tight">
				{{ title }}
			</h3>
			<p
				v-if="description"
				class="text-base text-white"
			>
				{{ description }}
			</p>

			<a
				class="cta-button text-sm lg:text-base leading-none mb-4 lg:mb-0"
				:href="trackingLink"
				target="_blank"
				rel="noopener noreferrer"
				@click.passive="onAdClick"
			>
				{{ cta }}
			</a>
		</div>
	</ClientOnly>
</template>

<script setup>
const SLOT_ID = "c26b2507-1c06-43a3-bf84-32efaf76127e";
const AIZEN_BASE_URL = "https://aizen-ad-manager.up.railway.app";
const EMERGENCY_FALLBACK_URL =
	"https://cfn.stockstotrade.com/money-bar-indicator-vsl?utm_source=Sidebar&utm_medium=web_ads&utm_campaign=6FZu9D";

const ad = useState(`aizen-ad-${SLOT_ID}`, () => null);
const adLoading = useState(`aizen-ad-loading-${SLOT_ID}`, () => false);
const impressionTracked = useState(
	`aizen-ad-impression-${SLOT_ID}`,
	() => false
);

const adRoot = ref(null);

const normalizeAizenUrl = (url) => {
	if (!url) {
		return null;
	}
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	return `${AIZEN_BASE_URL}${url}`;
};

const { pageKey } = usePageKey()
const adCtx = computed(() => ({ pageKey: pageKey.value, surface: "sidebar" }))
const trackingLink = computed(() =>
	buildAdClickUrl(
		normalizeAizenUrl(ad.value?.clickUrl) || ad.value?.destinationUrl || EMERGENCY_FALLBACK_URL,
		adCtx.value
	)
);
const onAdClick = () => fireAdClickEvent(adCtx.value)

const imageUrl = computed(() => ad.value?.imageUrl || "");

const title = computed(() => {
	const properties = ad.value?.properties || {};
	return properties.title || ad.value?.name || "";
});

const description = computed(() => {
	const properties = ad.value?.properties || {};
	return properties.body || "";
});

const cta = computed(() => {
	const properties = ad.value?.properties || {};
	return (
		properties.cta_label || properties.cta || properties.CTA || "Discover More"
	);
});

useAizenImpression(
	adRoot,
	() => normalizeAizenUrl(ad.value?.impressionUrl),
	{ firedRef: impressionTracked }
);

const fetchAd = async () => {
	if (ad.value || adLoading.value) {
		return;
	}

	adLoading.value = true;
	try {
		const response = await $fetch(
			`${AIZEN_BASE_URL}/api/slots/${SLOT_ID}/serve`
		);

		if (response && (response.clickUrl || response.destinationUrl)) {
			ad.value = response;
		}
	} catch {
	} finally {
		adLoading.value = false;
	}
};

const scheduleFetch = () => {
	const load = () => {
		fetchAd();
	};

	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(load, { timeout: 2000 });
		return;
	}

	window.setTimeout(load, 0);
};

onMounted(() => {
	if (process.client) {
		scheduleFetch();
	}
});
</script>

<style lang="scss">
@use "~/assets/utils" as *;

.stock-alert-card {
	border-radius: 1rem;
	overflow: hidden;
	position: relative;
	background: linear-gradient(90deg, #50b5ff 0%, #be55ff 99.95%);
	border-radius: 16px;
	gap: 16px;
	padding: 20px;
	display: flex;
	flex-direction: column;

	@include breakpoint(lg) {
		&.lg\:hidden {
			display: none;
		}
	}
}

.cta-button {
	background-color: white;
	color: black;
	font-weight: 700;
	padding: 0.75rem 2rem;
	border-radius: 0.75rem;
	border: none;
	cursor: pointer;
	box-shadow:
		0 10px 15px -3px rgba(0, 0, 0, 0.1),
		0 4px 6px -2px rgba(0, 0, 0, 0.05);
	transition: background-color 0.2s ease;
	text-align: center;
	text-decoration: none;
}

.cta-button:hover {
	background-color: #f3f4f6;
}
</style>
